import React, { useState } from 'react';
import {AutoForm, CenterSpinner} from '@soap/baseweb'
import {useLazyQuery} from '@soap/modules'
import {ParagraphMedium, ParagraphSmall, HeadingSmall} from "baseui/typography";
import {useStyletron} from "baseui";
import {SpaceTopAndBottom} from "../../../z40-uicomp/layout";
import {Roles} from "../../../hooks/useEnumerations";
import {Card, CardContent} from "../../ui/card";
import { CloseAccountDialog } from "../dialogs/CloseAccountDialog";
import { Button } from "../../ui/button";
import { ChevronDown } from "lucide-react";
import { cn } from "../../lib/utils";

export function Account() {

    const [getProfile, e138GotProfile] = useLazyQuery("C166v1_GetUserProfile", {});
    const [showCloseAccountDialog, setShowCloseAccountDialog] = useState(false);
    const [accountClosureExpanded, setAccountClosureExpanded] = useState(false);

    //* styling 
    const [css, theme] = useStyletron();


    if (e138GotProfile) {

        const profileView = {
            FirstName: e138GotProfile.e138_FirstName,
            LastName: e138GotProfile.e138_LastName,
            Email: e138GotProfile.e138_Email,
            QueuedPlan: e138GotProfile.e138_QueuedPlan,
            AuthenticationProvider: e138GotProfile.e138_Provider.e138_AuthenticationProvider,
            HasJoinedOrganisation: e138GotProfile.e138_IsOnAPlanWithSubAccounts,
            TenantName: e138GotProfile.e138_TenantName,
            RoleKeys: e138GotProfile.e138_RoleKeys,
            CurrentPlan: e138GotProfile.e138_ActivePlan,
        };

        return (
            <div className="space-y-4">
                {/* Account Details Card */}
                <SpaceTopAndBottom>
                    <Card>
                        <CardContent className="p-6">
                            <div className="space-y-4">
                                <div className="text-center">
                                    <HeadingSmall>Your Details</HeadingSmall>
                                </div>
                                {(e138GotProfile?.e138_Provider.e138_IsExternal === false) ?
                                    <>
                                        {profileView.HasJoinedOrganisation && (
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Account</span>
                                                <span className="text-gray-900">{profileView.TenantName}</span>
                                            </div>
                                        )}
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Email</span>
                                                <span className="text-gray-900 font-medium">{profileView.Email}</span>
                                            </div>
                                        </div>
                                        <AutoForm
                                            query={{
                                                $type: "C165v1_GetC164FormData"
                                            }}
                                            submitText={"Update Contact Details"}
                                            afterSubmit={() => {
                                                window.location.reload();
                                            }}
                                        />
                                    </>
                                    :
                                    <>
                                        <div className="space-y-3">
                                            {profileView.HasJoinedOrganisation && (
                                                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="font-medium text-gray-700">Account</span>
                                                    <span className="text-gray-900">{profileView.TenantName}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Email</span>
                                                <span className="text-gray-900">{profileView.Email}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">First Name</span>
                                                <span className="text-gray-900">{profileView.FirstName}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Last Name</span>
                                                <span className="text-gray-900">{profileView.LastName}</span>
                                            </div>
                                            <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                <span className="font-medium text-gray-700">Authentication Provider</span>
                                                <span className="text-gray-900">{profileView.AuthenticationProvider}</span>
                                            </div>
                                            
                                        </div>
                                        <SpaceTopAndBottom>
                                            <ParagraphSmall className={css({})}>If you wish to edit your profile details please goto
                                                your {e138GotProfile.e138_Provider.e138_AuthenticationProvider} account to change
                                                them.<br/>
                                                They will be updated on your next login.</ParagraphSmall>
                                        </SpaceTopAndBottom>
                                    </>
                                }
                                
                                {(profileView.RoleKeys.includes(Roles.OrgAdmin) || e138GotProfile.e138_IsOnNormalFreePlan === true) && (
                                    <div className="pt-6 border-t border-gray-200">
                                        <button
                                            type="button"
                                            onClick={() => setAccountClosureExpanded((open) => !open)}
                                            className="flex w-full items-center justify-between gap-2 rounded-md border border-gray-200 bg-muted/40 px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                                            aria-expanded={accountClosureExpanded}
                                        >
                                            <span>Close your account</span>
                                            <ChevronDown
                                                className={cn(
                                                    "h-4 w-4 shrink-0 text-gray-500 transition-transform",
                                                    accountClosureExpanded && "rotate-180"
                                                )}
                                                aria-hidden
                                            />
                                        </button>
                                        {accountClosureExpanded && (
                                            <div className="mt-3 rounded-md border border-gray-100 bg-gray-50/60 px-4 py-4 text-center">
                                                <p className="mb-4 text-sm text-gray-600">
                                                    Permanently close your account and remove your data. This cannot be undone.
                                                </p>
                                                <Button
                                                    variant="destructive"
                                                    onClick={() => setShowCloseAccountDialog(true)}
                                                >
                                                    Close Account
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </SpaceTopAndBottom>

                {showCloseAccountDialog && (
                    <CloseAccountDialog onClose={() => setShowCloseAccountDialog(false)} />
                )}
            </div>
        );
    } else {
            return <CenterSpinner/>;
    }
}